// The Usage screen's figures: the 30-day window, token totals, the error rate, installation health
// and the by-day rows and bars. Pure and DOM-free (tests/unit/usage/figures.test.ts). These were the
// "Usage tab" section of the AllCode admin's `screens/orgs/model.ts`, which keeps a copy for its other
// screens until they import the library's.
import type { components } from "../../api/schema";
import type { ChartBar } from "../../primitives/chart";
import { modelDisplayName } from "../../primitives/model-names";
import { formatUsd } from "../../primitives/money";
import { MONTH_SHORT_NAMES } from "../../primitives/months";

export type Installation = components["schemas"]["Installation"];
export type TimeseriesPoint = components["schemas"]["TimeseriesPoint"];
export type UsageTotals = Pick<
  components["schemas"]["UsageSummaryResponse"],
  | "requests"
  | "errors"
  | "inputTokens"
  | "outputTokens"
  | "cacheReadTokens"
  | "cacheWriteTokens"
  | "cacheWrite1hTokens"
  | "costMicroUsd"
  | "priceMicroUsd"
  | "costByClassMicroUsd"
  | "priceByClassMicroUsd"
  | "unpricedModels"
>;

/** One money figure split by token class (API-581); `null` where the server withheld the figure. */
export type ClassMoney = components["schemas"]["ClassMoney"];

/** What a line's token-class breakdown reads (SCR-584): its tokens, its two money figures and their split. */
export type ClassLine = Pick<
  components["schemas"]["UsageTotals"],
  | "inputTokens"
  | "outputTokens"
  | "cacheReadTokens"
  | "cacheWriteTokens"
  | "cacheWrite1hTokens"
  | "costMicroUsd"
  | "priceMicroUsd"
  | "costByClassMicroUsd"
  | "priceByClassMicroUsd"
> & {
  /** The models whose usage the money leaves out (REQ-017); empty when it is whole. */
  unpricedModels: readonly string[];
};

/** The five token classes, in the order every breakdown lists them (ADR-111). */
export const USAGE_TOKEN_CLASSES = ["input", "output", "cacheRead", "cacheWrite5m", "cacheWrite1h"] as const;
export type UsageTokenClass = (typeof USAGE_TOKEN_CLASSES)[number];

export interface ClassRow {
  class: UsageTokenClass;
  tokens: number;
  /** The class's AWS list cost, `null` when the server sent no split (the money is hidden). */
  cost: number | null;
  price: number | null;
}

export const USAGE_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `YYYY-MM-DD` bounds for "usage over the last 30 days" (API-013's `from`/`to`, UTC): the 30 UTC
 * days ending with today. `to` is exclusive and a bare date is its 00:00 UTC, so it is tomorrow —
 * today's date would leave out everything used today.
 */
export function usageWindow(now: Date): { from: string; to: string } {
  const tomorrow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  const to = new Date(tomorrow).toISOString().slice(0, 10);
  const from = new Date(tomorrow - USAGE_WINDOW_DAYS * DAY_MS).toISOString().slice(0, 10);
  return { from, to };
}

export function totalTokens(totals: Pick<UsageTotals, "inputTokens" | "outputTokens" | "cacheReadTokens" | "cacheWriteTokens">): number {
  return totals.inputTokens + totals.outputTokens + totals.cacheReadTokens + totals.cacheWriteTokens;
}

/**
 * A line's five token classes with their money as the server split it (REQ-233). The 1-hour cache
 * write is part of `cacheWriteTokens`, so the 5-minute write is the difference and the five rows
 * add up to `totalTokens` — never to it plus the 1-hour part again.
 */
export function classRows(line: Omit<ClassLine, "costMicroUsd" | "priceMicroUsd" | "unpricedModels">): ClassRow[] {
  const tokens: Record<UsageTokenClass, number> = {
    input: line.inputTokens,
    output: line.outputTokens,
    cacheRead: line.cacheReadTokens,
    cacheWrite5m: line.cacheWriteTokens - line.cacheWrite1hTokens,
    cacheWrite1h: line.cacheWrite1hTokens,
  };
  return USAGE_TOKEN_CLASSES.map((tokenClass) => ({
    class: tokenClass,
    tokens: tokens[tokenClass],
    cost: line.costByClassMicroUsd?.[tokenClass] ?? null,
    price: line.priceByClassMicroUsd?.[tokenClass] ?? null,
  }));
}

/** The class breakdown's fields of any report line (a summary, a by-model/team/user item, an explorer row). */
export function classLine(totals: Omit<ClassLine, "unpricedModels"> & { unpricedModels?: readonly string[] | undefined }): ClassLine {
  return {
    inputTokens: totals.inputTokens,
    outputTokens: totals.outputTokens,
    cacheReadTokens: totals.cacheReadTokens,
    cacheWriteTokens: totals.cacheWriteTokens,
    // Required on the wire; a line without it reads as no 1-hour writes rather than breaking the screen.
    cacheWrite1hTokens: totals.cacheWrite1hTokens ?? 0,
    costMicroUsd: totals.costMicroUsd,
    priceMicroUsd: totals.priceMicroUsd,
    // Required on the wire (A6); a line from a server older than API-581 has none: no split shown.
    costByClassMicroUsd: totals.costByClassMicroUsd ?? null,
    priceByClassMicroUsd: totals.priceByClassMicroUsd ?? null,
    unpricedModels: totals.unpricedModels ?? [],
  };
}

/** Basis points of requests that errored; `null` with no requests in the window (nothing to divide). */
export function errorRateBp(totals: Pick<UsageTotals, "requests" | "errors">): number | null {
  return totals.requests > 0 ? Math.round((totals.errors / totals.requests) * 10_000) : null;
}

/**
 * What a partial money figure leaves out (REQ-017): the line used these models on a day they had no
 * price, so its figure is the priced models' cost alone. Names them by display name, de-duplicated,
 * never by id; empty for a fully priced line. The marker's accessible name and hover title.
 */
export function unpricedTitle(unpricedModels: readonly string[]): string {
  const names = [...new Set(unpricedModels.map(modelDisplayName))];
  return names.length === 0 ? "" : `Excludes usage of ${names.join(", ")} (no price)`;
}

export type InstallationHealth = "healthy" | "stale" | "unknown" | "revoked";

/** Generous relative to any expected heartbeat interval: this is an at-a-glance signal, not an alarm. */
const STALE_AFTER_MS = 15 * 60 * 1000;

export function installationHealth(installation: Installation, now: Date): InstallationHealth {
  if (installation.status === "revoked") return "revoked";
  if (!installation.lastSeenAt) return "unknown";
  return now.getTime() - Date.parse(installation.lastSeenAt) <= STALE_AFTER_MS ? "healthy" : "stale";
}

export interface InstallationHealthSummary {
  healthy: number;
  stale: number;
  unknown: number;
  revoked: number;
}

export function installationsHealthSummary(installations: readonly Installation[], now: Date): InstallationHealthSummary {
  const summary: InstallationHealthSummary = { healthy: 0, stale: 0, unknown: 0, revoked: 0 };
  for (const installation of installations) summary[installationHealth(installation, now)]++;
  return summary;
}

/** "3 Sep 2026" — a UTC calendar day in words. */
export function dayLabel(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${Number(day)} ${MONTH_SHORT_NAMES[Number(month) - 1] ?? month} ${year}`;
}

/** 1234 → "1,234". */
export function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

/** 1_250_000 → "1.25M" for a compact card or axis. */
export function formatCompactCount(value: number): string {
  const magnitude = Math.abs(value);
  const trim = (text: string) => (text.includes(".") ? text.replace(/\.?0+$/, "") : text);
  if (magnitude >= 1_000_000_000) return `${trim((value / 1_000_000_000).toFixed(2))}B`;
  if (magnitude >= 1_000_000) return `${trim((value / 1_000_000).toFixed(2))}M`;
  if (magnitude >= 1_000) return `${trim((value / 1_000).toFixed(1))}k`;
  return String(Math.round(value));
}

export interface DailyUsageRow {
  /** `YYYY-MM-DD` (UTC). */
  day: string;
  requests: number;
  tokens: number;
  costMicroUsd: number | null;
  priceMicroUsd: number | null;
  /** The models whose usage the day's money leaves out; empty when it is whole. */
  unpricedModels: string[];
}

/** At most this many rows, whatever the window asks. */
const MAX_DAYS = 400;

/**
 * One row per UTC day of the window, oldest first, from a day-grain time series: a day with no
 * bucket is a zero day (the API sends only buckets with usage). `to` is exclusive. No "$0.00" is
 * invented where the money is hidden: a zero day's cost is $0 only when the series' buckets show
 * cost at all (the org's cost visibility holds for the whole window); its price is $0 only when a
 * bucket of the same UTC month shows price, since whether a month is billed is decided per month
 * (REQ-051) and a window can span a billed and a not-billed month. Otherwise it stays null.
 */
export function dailyUsageRows(points: readonly TimeseriesPoint[], from: string, to: string): DailyUsageRow[] {
  const byDay = new Map(points.map((point) => [point.bucket.slice(0, 10), point]));
  const gapCost = points.some((point) => point.costMicroUsd !== null) ? 0 : null;
  const pricedMonths = new Set(points.filter((point) => point.priceMicroUsd !== null).map((point) => utcMonth(point.bucket)));
  const rows: DailyUsageRow[] = [];
  const end = Date.parse(`${to}T00:00:00Z`);
  for (let time = Date.parse(`${from}T00:00:00Z`); time < end && rows.length < MAX_DAYS; time += DAY_MS) {
    const day = new Date(time).toISOString().slice(0, 10);
    const point = byDay.get(day);
    rows.push(
      point
        ? { day, requests: point.requests, tokens: totalTokens(point), costMicroUsd: point.costMicroUsd, priceMicroUsd: point.priceMicroUsd, unpricedModels: point.unpricedModels ?? [] }
        : { day, requests: 0, tokens: 0, costMicroUsd: gapCost, priceMicroUsd: pricedMonths.has(utcMonth(day)) ? 0 : null, unpricedModels: [] },
    );
  }
  return rows;
}

/** `YYYY-MM` of an ISO day or timestamp (UTC). */
function utcMonth(iso: string): string {
  return iso.slice(0, 7);
}

/** The chart's bars: tokens per day, the exact figures in each bar's tooltip, a partial cost said to be one. */
export function dailyUsageBars(rows: readonly DailyUsageRow[]): ChartBar[] {
  return rows.map((row) => ({
    key: row.day,
    label: String(Number(row.day.slice(8, 10))),
    tooltip: `${dayLabel(row.day)}: ${formatCount(row.tokens)} tokens, ${formatCount(row.requests)} requests${costText(row)}`,
    segments: [{ series: "tokens", value: row.tokens }],
  }));
}

function costText(row: DailyUsageRow): string {
  if (row.costMicroUsd === null) return "";
  const partial = row.unpricedModels.length > 0 ? ` (${lowerFirst(unpricedTitle(row.unpricedModels))})` : "";
  return `, AWS list cost ${formatUsd(row.costMicroUsd)}${partial}`;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}
